import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import {
  Eye,
  EyeOff,
  Lock,
  User,
  ArrowRight,
  TrendingUp,
  Shield,
  BarChart3,
  Sparkles,
} from 'lucide-react';
import { Card } from '@/shared/ui/card/Card';
import { Input } from '@/shared/ui/input/Input';
import { Button } from '@/shared/ui/button/Button';
import { useLogin } from './hooks';

const schema = z.object({
  identifier: z.string().min(1, 'Ingresa tu correo o cédula'),
  password: z.string().min(1, 'Ingresa tu contraseña'),
});

type FormData = z.infer<typeof schema>;

const features = [
  {
    icon: BarChart3,
    label: 'Estados financieros',
    desc: 'Multi-compañía en tiempo real',
  },
  {
    icon: TrendingUp,
    label: 'Análisis gerencial',
    desc: 'KPIs y proyecciones',
  },
  {
    icon: Shield,
    label: 'Auditoría',
    desc: 'Trazabilidad completa',
  },
];

function AuroraWaves() {
  return (
    <svg
      viewBox="0 0 1440 320"
      className="pointer-events-none absolute bottom-0 left-0 w-full opacity-40"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="aurora-wave-1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(16,185,129,0.7)" />
          <stop offset="50%" stopColor="rgba(14,165,233,0.7)" />
          <stop offset="100%" stopColor="rgba(139,92,246,0.6)" />
        </linearGradient>
        <linearGradient id="aurora-wave-2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(14,165,233,0.6)" />
          <stop offset="50%" stopColor="rgba(236,72,153,0.5)" />
          <stop offset="100%" stopColor="rgba(16,185,129,0.6)" />
        </linearGradient>
        <linearGradient id="aurora-wave-3" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(139,92,246,0.5)" />
          <stop offset="50%" stopColor="rgba(16,185,129,0.5)" />
          <stop offset="100%" stopColor="rgba(14,165,233,0.5)" />
        </linearGradient>
      </defs>
      <motion.path
        initial={{ d: 'M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z' }}
        animate={{
          d: [
            'M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,192L48,202.7C96,213,192,235,288,229.3C384,224,480,192,576,181.3C672,171,768,181,864,202.7C960,224,1056,256,1152,245.3C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        fill="url(#aurora-wave-1)"
      />
      <motion.path
        initial={{ d: 'M0,288L48,272C96,256,192,224,288,213.3C384,203,480,213,576,234.7C672,256,768,288,864,282.7C960,277,1056,235,1152,213.3C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z' }}
        animate={{
          d: [
            'M0,288L48,272C96,256,192,224,288,213.3C384,203,480,213,576,234.7C672,256,768,288,864,282.7C960,277,1056,235,1152,213.3C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,256L48,261.3C96,267,192,277,288,266.7C384,256,480,224,576,218.7C672,213,768,235,864,261.3C960,288,1056,320,1152,309.3C1248,299,1344,256,1392,234.7L1440,213L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,288L48,272C96,256,192,224,288,213.3C384,203,480,213,576,234.7C672,256,768,288,864,282.7C960,277,1056,235,1152,213.3C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
          ],
        }}
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        fill="url(#aurora-wave-2)"
        opacity="0.7"
      />
      <motion.path
        initial={{ d: 'M0,320L48,309.3C96,299,192,277,288,277.3C384,277,480,299,576,309.3C672,320,768,320,864,309.3C960,299,1056,277,1152,272C1248,267,1344,277,1392,282.7L1440,288L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z' }}
        animate={{
          d: [
            'M0,320L48,309.3C96,299,192,277,288,277.3C384,277,480,299,576,309.3C672,320,768,320,864,309.3C960,299,1056,277,1152,272C1248,267,1344,277,1392,282.7L1440,288L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,320L48,314.7C96,309,192,299,288,293.3C384,288,480,288,576,298.7C672,309,768,331,864,325.3C960,320,1056,288,1152,282.7C1248,277,1344,299,1392,309.3L1440,320L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,320L48,309.3C96,299,192,277,288,277.3C384,277,480,299,576,309.3C672,320,768,320,864,309.3C960,299,1056,277,1152,272C1248,267,1344,277,1392,282.7L1440,288L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
          ],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        fill="url(#aurora-wave-3)"
        opacity="0.5"
      />
    </svg>
  );
}

export function LoginPage() {
  const { login, isLoading, error } = useLogin();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    await login({ usuario: data.identifier, password: data.password });
  };

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-bg font-body">
      {/* Panel izquierdo - Aurora Borealis branding */}
      <motion.div
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' as const }}
        className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-aurora-borealis p-12 text-text-inverse lg:flex"
      >
        {/* Capas de aurora animadas */}
        <div className="aurora-layer aurora-layer-1" />
        <div className="aurora-layer aurora-layer-2" />
        <div className="aurora-layer aurora-layer-3" />
        <div className="aurora-sheen" />

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-500 shadow-lg shadow-emerald-500/25">
              <Sparkles className="h-7 w-7 text-white" />
            </div>
            <span className="font-display text-2xl font-bold tracking-tight">
              AURORA
            </span>
          </div>
        </div>

        <div className="relative z-10 max-w-lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 backdrop-blur-md"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-xs font-medium tracking-wide text-slate-300">
              Plataforma ejecutiva activa
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-display text-6xl font-bold leading-[1.05] tracking-tight text-white text-glow"
          >
            AURORA
          </motion.h1>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="mt-3 font-display text-3xl font-semibold tracking-tight text-emerald-300"
          >
            Estados Financieros
            <span className="block text-cyan-300">Multi-Compañía</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-6 text-lg text-slate-300"
          >
            Ilumina la toma de decisiones con datos financieros claros,
            comparables y actualizados de todas las compañías del grupo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="mt-10 space-y-4"
          >
            {features.map((feature, i) => (
              <motion.div
                key={feature.label}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + i * 0.12, duration: 0.5 }}
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm transition-colors hover:bg-white/10"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-400/20 to-cyan-400/20 text-emerald-300">
                  <feature.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-display font-semibold">{feature.label}</p>
                  <p className="text-sm text-slate-400">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="relative z-10 text-sm text-slate-400"
        >
          © {new Date().getFullYear()} AURORA by Grupo Coris. Todos los derechos
          reservados.
        </motion.p>

        {/* Ondas de aurora boreal */}
        <AuroraWaves />
      </motion.div>

      {/* Panel derecho - formulario claro */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden bg-bg p-4 sm:p-8">
        {/* Toques sutiles de aurora en las esquinas */}
        <div className="pointer-events-none absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-cyan-400/10 blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' as const }}
          className="relative z-10 w-full max-w-md"
        >
          <Card className="border border-border bg-white p-8 shadow-elevated sm:p-10">
            <div className="mb-8 text-center">
              <div className="mb-6 flex justify-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-500 shadow-lg shadow-emerald-500/25 lg:hidden">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                <img
                  src="/images/logo_gc.png"
                  alt="Grupo Coris"
                  className="hidden h-16 w-auto object-contain lg:block"
                />
              </div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-text-primary">
                Bienvenido a <span className="text-emerald-500">AURORA</span>
              </h2>
              <p className="mt-2 text-text-secondary">
                Ingresa tus credenciales para continuar
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="space-y-1.5">
                <label
                  htmlFor="identifier"
                  className="text-xs font-semibold uppercase tracking-wider text-text-secondary"
                >
                  Correo o Cédula
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-text-muted" />
                  <Input
                    {...register('identifier')}
                    id="identifier"
                    type="text"
                    className="h-12 rounded-xl border-border bg-surface pl-12 text-base text-text-primary placeholder:text-text-muted focus:border-emerald-500/40 focus:ring-emerald-500/10"
                    placeholder="usuario@grupocoris.com"
                    autoComplete="username"
                  />
                </div>
                {errors.identifier && (
                  <p className="text-xs text-danger">{errors.identifier.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="password"
                  className="text-xs font-semibold uppercase tracking-wider text-text-secondary"
                >
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-text-muted" />
                  <Input
                    {...register('password')}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="h-12 rounded-xl border-border bg-surface pl-12 pr-12 text-base text-text-primary placeholder:text-text-muted focus:border-emerald-500/40 focus:ring-emerald-500/10"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-text-muted transition-colors hover:bg-surface-elevated hover:text-text-primary"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-danger">{errors.password.message}</p>
                )}
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-danger/20 bg-danger-bg p-4 text-sm text-danger"
                >
                  {error}
                </motion.div>
              )}

              <Button
                type="submit"
                className="h-12 w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-base font-semibold text-white shadow-lg shadow-emerald-500/20 transition-all hover:from-emerald-400 hover:to-cyan-400 hover:shadow-emerald-500/30"
                loading={isLoading}
              >
                Iniciar sesión
                <ArrowRight className="h-5 w-5" />
              </Button>
            </form>

            <p className="mt-6 text-center text-xs text-text-muted lg:hidden">
              © {new Date().getFullYear()} AURORA by Grupo Coris.
            </p>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
